import { z } from 'zod';
/**
 * Reusable Validation Schemas
 *
 * Provides runtime validation for user inputs to prevent:
 * - SQL injection attempts
 * - Malformed data (objects/arrays instead of strings)
 * - Invalid UUID or slug formats
 *
 * Exports reusable validation schemas with strict type checking,
 * length limits, and format validation.
 */
/**
 * UUID validation (strict format)
 * Rejects empty strings to prevent race conditions
 */
export const uuidSchema = z.string().min(1, 'ID is required').uuid('Invalid UUID format');
/**
 * Slug validation (alphanumeric + hyphens only, lowercase)
 */
export const slugSchema = z
    .string()
    .min(3, 'Slug must be at least 3 characters')
    .max(50, 'Slug must be at most 50 characters')
    .regex(/^[a-z0-9-]+$/, 'Slug must contain only lowercase letters, numbers, and hyphens');
/**
 * Sanitized string with trim (default max length 500)
 */
export const sanitizedString = (maxLength = 500) => z
    .string()
    .trim()
    .max(maxLength, `String must be at most ${maxLength} characters`);
/**
 * Short string (for names, titles, etc.)
 */
export const shortString = sanitizedString(255);
/**
 * Medium string (for descriptions, notes, etc.)
 */
export const mediumString = sanitizedString(1000);
/**
 * Long string (for long-form text)
 */
export const longString = sanitizedString(5000);
/**
 * Positive integer
 */
export const positiveInt = z
    .number()
    .int('Must be an integer')
    .positive('Must be positive');
/**
 * Non-negative integer (0 or more)
 */
export const nonNegativeInt = z
    .number()
    .int('Must be an integer')
    .nonnegative('Must be non-negative');
/**
 * Email validation
 */
export const emailSchema = z
    .string()
    .email('Invalid email format')
    .toLowerCase()
    .trim();
/**
 * URL validation (shopify domains)
 */
export const shopifyDomainSchema = z
    .string()
    .regex(/^[a-z0-9-]+\.myshopify\.com$/, 'Must be a valid Shopify domain (e.g., store.myshopify.com)')
    .max(100, 'Domain too long');
